<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { AnimationEventHandler } from "svelte/elements";
	import { fade } from "svelte/transition";
	import zoomIcon from "@core/assets/icons/zoom.svg?raw";
	import { createScrollLock } from "../../state/lockScroll.svelte.js";
	import AlgoliaResults from "./AlgoliaResults.svelte";
	import translations from "./AlgoliaSearchbar.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	// Ensure the overlay appears above everything else
	const safeZIndex = 120;

	let {
		open = $bindable(false),
		value = $bindable(""),
		/* eslint-disable prefer-const */
		transparent,
		hasInfoBanner,
		/* eslint-enable prefer-const */
	}: { open?: boolean; value?: string; transparent: boolean; hasInfoBanner: boolean } = $props();

	$effect(() => {
		if (!open) {
			value = "";
		}
	});

	const scrollLock = createScrollLock();
	$effect(() => {
		scrollLock.locked = open;
	});

	const onGrowEnd: AnimationEventHandler<HTMLDivElement> = (event): void => {
		event.currentTarget.querySelector("input")?.focus();
	};
</script>

{#if !open}
	<button
		type="button"
		class={[
			"border rounded-full transition-colors",
			transparent
				? "border-white/16 text-white"
				: "border-fcrlightgray-300 bg-fcrlightgray-200 hover:border-fcrgray text-fcrgray",
			"h-14 w-18 hidden lg:flex justify-center items-center",
		]}
		onclick={(): void => {
			open = true;
		}}
		aria-label={translate("openSearch")}
	>
		<Icon icon={zoomIcon} class="size-6" />
	</button>
{:else}
	<div style:z-index={safeZIndex + 1} class="w-18 min-w-fit max-w-full relative">
		<label for="websearch" class="bg-fcrlightgray-200 text-fcrdarkgray rounded-full px-4 py-3 lg:py-4 flex relative">
			<div class="animate-grow" onanimationend={onGrowEnd}>
				<input
					bind:value
					id="websearch"
					placeholder={translate("placeholder")}
					class={[
						"text-base placeholder:text-fcrgray bg-fcrlightgray-200 absolute inset-0 size-full",
						"px-4 py-3 lg:py-4 rounded-full !outline-none border border-fcrlightgray-300",
					]}
				/>
			</div>
			<div style:z-index={safeZIndex + 1}>
				<Icon icon={zoomIcon} class="size-6" />
			</div>
		</label>
		{#if value.length > 0}
			<output
				class="absolute top-full translate-y-2 bg-white rounded-2xl p-2 border border-fcrlightgray-300"
				transition:fade={{ duration: 150 }}
			>
				<div class="flex flex-col max-h-[min(90vh,20rem)] w-78 sm:w-105 overflow-y-auto">
					<AlgoliaResults searchText={value} />
				</div>
			</output>
		{/if}
	</div>

	<button
		type="button"
		onclick={(): void => {
			open = false;
		}}
		style:z-index={safeZIndex}
		aria-label={translate("closeSearch")}
		class={[hasInfoBanner ? "top-24" : "top-14", "fixed lg:top-0 inset-0 bg-[#332D2B3D] appearance-none cursor-auto"]}
	></button>
{/if}

<style>
	@keyframes grow {
		from {
			width: 0;
		}

		to {
			width: 15.75rem;
		}
	}

	div.animate-grow {
		animation: grow 400ms ease-in-out forwards;
	}
</style>
